<script lang="ts">
	import { onMount, tick } from 'svelte';
	import { get } from 'svelte/store';
	import { push, replace } from 'svelte-spa-router';
	import { format } from 'src/services/i18n';

	import { profiles, showProfileModal } from 'src/stores/features/profiles';
	import { user_data, user_settings } from 'src/stores/account/user';
	import { handleToken, handle_base_url } from 'src/services/http';

	import { onChangeProfile } from 'src/components/Profiles/profiles_helpers';

	import { DropdownMenu, RectangularAvatar, toast, Modal } from '@Shelly/shelly-design-system';

	import LoginModal from './LoginModal.svelte';
	import LogoutModal from './LogoutModal.svelte';
	import SetupProfileModal from './SetupProfileModal.svelte';
	import { mobile } from 'src/services/mobile-rework';
	import type { MenuListDataType, PendingProfileSwitch } from './DropdownItem.types';
	import LoginAnotherAccModal from './LoginAnotherAccModal.svelte';
	import ProfileDropdownContent from './ProfileDropdownContent.svelte';
	import UnitSelectionModal from 'src/components/UnitSelectionModal.svelte';
	import { switchUnit, HomeUnavailableError } from 'src/services/auth_v2';
	import { addUnit } from 'src/services/iotu/v2_users_auth';
	import { canAddOwnedHome, getProfileDisplayName } from 'src/helpers/houses/homes_helpers';
	import { MAX_OWNED_HOMES } from 'src/constants';
	import type { AvailableUnits } from 'src/types/responses/usersAuthResponse';
	import type { Home } from 'src/types/Home';
	import InstallerLogoutModal from './InstallerLogoutModal.svelte';
	import { handleLogout } from 'src/helpers/account_helpers';
	import { showGlobalLoader } from 'src/stores/ui/global-loader';
	import { fillMissingUnitNames } from 'src/helpers/utilities';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';
	import { currentUnitId, homes, refreshHomes, removeHomeFromList } from 'src/stores/account/home';
	import { dropDownMenuItem } from 'src/helpers/main_menu';

	export let isPremium = false;
	export let scrollMaxHeight: number | null = 420;

	// Fallback icon shown on the trigger avatar when the active home has no
	// avatar image/color/initials to inherit.
	const HOME_FALLBACK_ICON = 'fa-solid fa-house-chimney';
	let showLoginModal = false;
	let showLogoutModal = false;
	let isLoading = false;
	let showLoginAnotherAccModal = false;
	let logoutFromAllProfiles = false;
	let showInstallerModal = false;
	let addProfileAfterInstallerLogout = false;
	let showUnitModal = false;
	let profileToChange: MenuListDataType | undefined;
	// Discovery credentials of the switch target are forwarded to the unit picker
	// so it can fetch that account's homes for avatars (FE-2159).
	let pendingProfileSwitch: PendingProfileSwitch | null = null;
	// Stores the original profile info before a switch attempt so we can restore
	// the session if the user cancels the unit selection modal.
	let originalProfileBeforeSwitch: {
		email: string;
		id: number;
		sub?: number;
	} | null = null;
	let windowClientWidth: number;

	// const currentProfile = dropdownItemsData.slice(-1)[0];
	let showProfileMenu = false;
	let showAccountModal = false;
	let logoutFromCurrentWihtLogin = false;

	$: is_mobile = windowClientWidth <= SystemScreenBreakpoints.MOBILE_MAX;
	$: is_tablet = windowClientWidth <= SystemScreenBreakpoints.TABLET_MAX;
	$: activeProfile = $profiles.find((item) => item.isSelected);
	// The trigger avatar shows the currently active home's image (not the
	// account/profile image). Falls back to the profile avatar's other props.
	$: activeHome = $homes.list.find((home) => home.isActive);
	$: unitModalProfile = pendingProfileSwitch
		? $profiles.find((profile) => profile.email === pendingProfileSwitch.email)
		: activeProfile;
	// Classic layout has its own "More" set: Discounts + Settings + Premium routes
	// (instead of new layout's Explore Shelly + /subscription).
	$: moreMenuItems = dropDownMenuItem;
	$: shouldUseModal = is_mobile || is_tablet;

	// Picker dismissed without a selection: drop the pending switch so later unit
	// modals render the active profile, and restore its session (FE-1484 / FE-2197).
	$: if (!showUnitModal && pendingProfileSwitch) {
		pendingProfileSwitch = null;
		originalProfileBeforeSwitch = null;
		restoreActiveSession();
	}

	// Re-point the HTTP client (base URL + token) at the active profile's session
	// held in `user_data`. Used when the unit picker is dismissed without a selection.
	const restoreActiveSession = (): void => {
		const ud = get(user_data);
		if (ud?.user_api_url) handle_base_url(ud.user_api_url);
		if (ud?.token) handleToken(ud.token);
	};

	let showHomeList = false;

	const onToggleProfileMenu = () => {
		if (shouldUseModal) {
			showAccountModal = !showAccountModal;
		} else {
			showProfileMenu = !showProfileMenu;
		}
	};

	const closeProfileMenu = () => {
		showProfileMenu = false;
		showAccountModal = false;
		showHomeList = false;
	};

	const handleChangeProfile = async (
		email: string,
		id: number,
		sub: number | undefined = undefined,
		unitId?: string,
		subId?: string,
		knownAvailableUnits?: AvailableUnits,
	) => {
		if (isLoading || (!unitId && email === activeProfile.email && sub === activeProfile.sub)) {
			return;
		}

		// Save the current active profile before attempting the switch so we can
		// restore the session if the user cancels the unit selection modal.
		const isNewProfileSwitch = !unitId && activeProfile;
		if (isNewProfileSwitch) {
			originalProfileBeforeSwitch = {
				email: activeProfile.email,
				id: activeProfile.id,
				sub: activeProfile.sub as number | undefined,
			};
		}

		isLoading = true;
		if (!unitId) closeProfileMenu();
		const result = await onChangeProfile(email, id, sub, unitId, subId, knownAvailableUnits);

		if (result && 'multipleUnits' in result) {
			pendingProfileSwitch = {
				email,
				id,
				sub,
				availableUnits: result.availableUnits,
				availableSubUnits: result.availableSubUnits,
				discoveryToken: result.discoveryToken,
				discoveryApiUrl: result.discoveryApiUrl,
			};
			showUnitModal = true;
			isLoading = false;
			return;
		}

		// Switch completed successfully, clear the original profile backup.
		originalProfileBeforeSwitch = null;
		pendingProfileSwitch = null;
		isLoading = false;
		replace('/');
	};

	const handleAddProfile = () => {
		if ($user_data?.installer) {
			closeProfileMenu();
			addProfileAfterInstallerLogout = true;
			profileToChange = undefined;
			showInstallerModal = true;
			return;
		}

		if ($profiles.length >= 5) {
			closeProfileMenu();
			toast.open({
				type: 'info',
				title: $format('alert_title_profile_limit_reached', {
					default: 'Profiles limit reached',
				}),
				text: $format('alert_text_profiles_limit_reached', {
					default: `You can not log in with more than 5 accounts at a time.`,
				}),
			});
		} else {
			onAddProfile();
		}
	};

	const onAddProfile = async (skipCloseProfileMenu = false) => {
		if (!skipCloseProfileMenu) {
			closeProfileMenu();
		}
		await tick();
		showLoginModal = true;
	};

	const onEditProfile = (e: MouseEvent | TouchEvent, editProfile: MenuListDataType) => {
		e.stopPropagation();

		showProfileModal.set(true);
		// showProfileModal = true;
		profiles.edit(editProfile);
		onToggleProfileMenu();
	};

	const onLogoutProfile = (e?: MouseEvent | TouchEvent) => {
		e?.stopPropagation();
		showLogoutModal = true;
		onToggleProfileMenu();
	};

	const onLogoutAllProfiles = () => {
		showLogoutModal = true;
		logoutFromAllProfiles = true;
		onToggleProfileMenu();
	};

	onMount(() => {
		if ($profiles.length == 0) {
			const title = getProfileDisplayName(
				$user_settings.name,
				$user_data.email ?? '',
				$user_data.user_id,
			);

			const newProfile: MenuListDataType = {
				title,
				acc_avatar: {
					text: title.slice(0, 1),
					backgroundColor: 'color-green',
					...$user_settings.acc_avatar,
					avatarOptions: {
						type: isPremium ? 'premium' : null,
					},
				},
				isSelected: true,
				id: $user_data.user_id,
				email: $user_data.email,
				pass: $user_data.password,
				installer: !!$user_data.installer,
			};

			profiles.add(newProfile);
		}
		// set mock data for testing
		// const currentMenuItemLists = getDataFromDB();
		// profiles.set(currentMenuItemLists);
	});

	const closeModalOnAndroidBack = (e: CustomEvent) => {
		if (showProfileMenu) {
			e.preventDefault();
			showProfileMenu = false;
		}
	};

	onMount(() => {
		if (mobile.isMobile) {
			mobile.addEventListener('back_button', closeModalOnAndroidBack);
			return () => mobile.removeEventListener('back_button', closeModalOnAndroidBack);
		}
	});

	const onProfileClick = (menuListItem: MenuListDataType) => {
		if ($user_data?.installer) {
			profileToChange = menuListItem;
			showInstallerModal = true;
			closeProfileMenu();
		} else {
			handleChangeProfile(
				menuListItem.email,
				menuListItem.id,
				menuListItem.sub as number | undefined,
			);
		}
	};

	const onUnitSelected = async (e: CustomEvent<{ unitId: string; subId?: number }>) => {
		showUnitModal = false;
		const { unitId, subId } = e.detail;

		if (pendingProfileSwitch) {
			const { email, id, sub, availableUnits } = pendingProfileSwitch;
			// Clear pending state so the cancel-restore reactive doesn't fire.
			pendingProfileSwitch = null;
			originalProfileBeforeSwitch = null;
			await handleChangeProfile(
				email,
				id,
				sub,
				unitId,
				subId != null ? String(subId) : undefined,
				availableUnits,
			);
			return;
		}

		if (!subId && unitId === $currentUnitId) {
			return;
		}
		isLoading = true;
		try {
			await switchUnit(unitId, subId != null ? String(subId) : undefined);
		} catch (err) {
			console.error('Error switching unit', err);
			handleSwitchError(err);
		} finally {
			isLoading = false;
		}
	};

	const onAddUnit = async () => {
		showUnitModal = false;
		if (!canAddOwnedHome($user_data.available_units)) {
			toast.open({
				type: 'warning',
				title: $format('alert_owned_homes_limit_reached', {
					values: { limit: MAX_OWNED_HOMES },
					default: `You can create up to ${MAX_OWNED_HOMES} homes. Delete an existing home to create a new one.`,
				}),
				duration: 4000,
			});
			return;
		}
		try {
			await addUnit(undefined, $user_data.lang, $user_data.timezone);
			toast.open({
				type: 'success',
				title: $format('success_add_unit', { default: 'New home added successfully' }),
			});
			void refreshHomes();
		} catch {
			toast.open({
				type: 'danger',
				title: $format('error_add_unit', { default: 'Failed to add new home' }),
			});
		}
	};

	const onHomeClick = async (home: Home) => {
		if (isLoading || home.isActive) {
			return;
		}

		closeProfileMenu();

		isLoading = true;
		showGlobalLoader.set(true);
		try {
			await switchUnit(home.id, home.sub != null ? String(home.sub) : undefined);
			replace('/');
		} catch (err) {
			console.error('Error switching home', err);
			handleSwitchError(err);
		} finally {
			isLoading = false;
			showGlobalLoader.set(false);
		}
	};

	/**
	 * Surface a precise message when a home switch fails because the target is
	 * gone (deleted / unshared / transferred), and drop the stale entry from the
	 * picker; otherwise fall back to the generic switch-failure toast (FE-2022).
	 */
	const handleSwitchError = (err: unknown): void => {
		if (err instanceof HomeUnavailableError) {
			removeHomeFromList(err.unitId, err.subId);
			void refreshHomes();
			toast.open({
				type: 'danger',
				title: $format('error_home_no_longer_available', {
					default: 'This home is no longer available and has been removed from your list',
				}),
			});
			return;
		}
		toast.open({
			type: 'danger',
			title: $format('error_switch_unit', { default: 'Failed to switch home' }),
		});
	};

	const goToHomeManagement = () => {
		closeProfileMenu();
		push('/settings/house?homeManagement');
	};

	const handleInstallerLogoutSwitch = async () => {
		$showGlobalLoader = true;
		const result = await handleLogout(profileToChange);
		$showGlobalLoader = false;

		if (addProfileAfterInstallerLogout) {
			addProfileAfterInstallerLogout = false;
			showLoginModal = true;
			return;
		}

		if (result && 'pendingUnitSelection' in result) {
			const targetEmail = $profiles.find((p) => p.id === result.profileId)?.email;
			if (!targetEmail) return;

			pendingProfileSwitch = {
				email: targetEmail,
				id: result.profileId,
				sub: result.profileSub as number | undefined,
				availableUnits: result.availableUnits,
				availableSubUnits: result.availableSubUnits,
				discoveryToken: result.discoveryToken,
				discoveryApiUrl: result.discoveryApiUrl,
			};
			showUnitModal = true;
		}
	};
</script>

<svelte:window bind:innerWidth={windowClientWidth} />

{#key activeProfile?.email || activeProfile?.acc_avatar}
	<!-- use key to force re-render on profile change to reload avatar -->
	<DropdownMenu
		bind:show={showProfileMenu}
		removeOverlay
		{scrollMaxHeight}
		width={300}
		maxWidth={300}
		on:close={() => {
			showHomeList = false;
		}}
	>
		<RectangularAvatar
			slot="trigger"
			class="cursor-pointer"
			size="s"
			text={activeHome?.avatar?.text || activeHome?.name?.charAt(0)}
			image={activeHome?.avatar?.image}
			icon={activeHome?.avatar === undefined && HOME_FALLBACK_ICON}
			backgroundColor={activeHome?.avatar?.backgroundColor}
			on:click={!isLoading && onToggleProfileMenu}
			disabled={isLoading}
			avatarOptions={{
				type: isPremium ? 'premium' : null,
				badgeProps: { show: isPremium },
			}}
		/>
		<ProfileDropdownContent
			{moreMenuItems}
			bind:showHomeList
			on:profileClick={(e) => onProfileClick(e.detail)}
			on:addProfile={() => handleAddProfile()}
			on:logoutAllProfiles={() => onLogoutAllProfiles()}
			on:homeClick={(e) => onHomeClick(e.detail)}
			on:editProfile={(e) => onEditProfile(e.detail.event, e.detail.profile)}
			on:logoutProfile={(e) => onLogoutProfile(e.detail)}
			on:closeMenu={closeProfileMenu}
			on:logout={() => onLogoutProfile()}
		/>
	</DropdownMenu>
{/key}

<Modal
	class="profile-account-modal"
	bind:showModal={showAccountModal}
	removeScrollPadding
	removeFooterPaddings
	on:close={() => {
		showAccountModal = false;
		showHomeList = false;
	}}
	hideTitle
	containerBottomPadding={is_mobile ? undefined : '--spacing-s'}
>
	<svelte:fragment slot="content">
		<ProfileDropdownContent
			{moreMenuItems}
			bind:showHomeList
			on:profileClick={(e) => onProfileClick(e.detail)}
			on:homeClick={(e) => onHomeClick(e.detail)}
			on:manageHome={goToHomeManagement}
			on:editProfile={(e) => onEditProfile(e.detail.event, e.detail.profile)}
			on:logoutProfile={(e) => onLogoutProfile(e.detail)}
			on:addProfile={() => handleAddProfile()}
			on:logoutAllProfiles={() => onLogoutAllProfiles()}
			on:closeMenu={closeProfileMenu}
			on:logout={() => onLogoutProfile()}
		/>
	</svelte:fragment>
</Modal>

{#if showLoginModal}
	<LoginModal bind:logoutFromCurrentWihtLogin bind:showModal={showLoginModal} />
{/if}

{#if $showProfileModal}
	<SetupProfileModal bind:showModal={$showProfileModal} />
{/if}

{#if showLogoutModal}
	<LogoutModal
		on:logout={() => {
			showLogoutModal = false;
			showLoginAnotherAccModal = true;
		}}
		on:selectUnit={(e) => {
			showLogoutModal = false;
			const targetEmail = $profiles.find((p) => p.id === e.detail.profileId)?.email;
			if (!targetEmail) return;
			pendingProfileSwitch = {
				email: targetEmail,
				id: e.detail.profileId,
				sub: e.detail.profileSub,
				availableUnits: e.detail.availableUnits,
				availableSubUnits: e.detail.availableSubUnits,
				discoveryToken: e.detail.discoveryToken,
				discoveryApiUrl: e.detail.discoveryApiUrl,
			};
			showUnitModal = true;
		}}
		bind:showModal={showLogoutModal}
		bind:logoutFromAllProfiles
	/>
{/if}

{#if showLoginAnotherAccModal}
	<LoginAnotherAccModal
		on:add_another_profile={() => {
			showLoginAnotherAccModal = false;
			onAddProfile(true);
			logoutFromCurrentWihtLogin = true;
		}}
		on:selectUnit={(e) => {
			showLoginAnotherAccModal = false;
			const targetEmail = $profiles.find((p) => p.id === e.detail.profileId)?.email;
			if (!targetEmail) return;
			pendingProfileSwitch = {
				email: targetEmail,
				id: e.detail.profileId,
				sub: e.detail.profileSub,
				availableUnits: e.detail.availableUnits,
				availableSubUnits: e.detail.availableSubUnits,
				discoveryToken: e.detail.discoveryToken,
				discoveryApiUrl: e.detail.discoveryApiUrl,
			};
			showUnitModal = true;
		}}
		bind:showModal={showLoginAnotherAccModal}
	/>
{/if}

<InstallerLogoutModal bind:showModal={showInstallerModal} on:switch={handleInstallerLogoutSwitch} />

{#if showUnitModal}
	<UnitSelectionModal
		bind:showModal={showUnitModal}
		selectedUnitId={pendingProfileSwitch ? undefined : $currentUnitId}
		hideActiveUnit={!pendingProfileSwitch}
		authToken={pendingProfileSwitch?.discoveryToken}
		userApiUrl={pendingProfileSwitch?.discoveryApiUrl}
		accountTitle={unitModalProfile?.title}
		accountEmail={pendingProfileSwitch?.email ?? unitModalProfile?.email ?? activeProfile?.email}
		units={pendingProfileSwitch
			? fillMissingUnitNames(pendingProfileSwitch.availableUnits)
			: ($user_data.available_units ?? {})}
		subUnits={pendingProfileSwitch
			? (pendingProfileSwitch.availableSubUnits ?? {})
			: ($user_data.available_sub_units ?? {})}
		showAddUnit={!pendingProfileSwitch &&
			!$user_data.is_sub &&
			canAddOwnedHome($user_data.available_units)}
		{isLoading}
		on:select={onUnitSelected}
		on:addUnit={onAddUnit}
	/>
{/if}

<style>
	@media screen and (max-width: 639px) {
		:global(.profile-account-modal.modal-container.type-default) {
			padding-right: 0 !important;
			padding-left: 0 !important;
			padding-bottom: 0 !important;
		}
	}
</style>
